import BookmarkOutlinedIcon from "@mui/icons-material/BookmarkOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Alert, Box, Button, CircularProgress, Link, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { downloadFile } from "../../utils/download-files";
import { useGenerateCommsContext } from "./generate-comms-context";
import { useTemplateImageDimensions } from "./use-comms-template-image-dimensions";

import type { FC } from "react";

export const CommsTemplateImage: FC<{ format: string }> = ({ format }) => {
  const {
    saveImage,
    previewImages: { images, selectedImage },
    branding: { selectedImageType },
  } = useGenerateCommsContext();
  const loader = images?.[selectedImage];
  const dimensions = useTemplateImageDimensions(selectedImageType);
  const [saving, setSaving] = useState(false);

  const imageUrl = !loader || loader.loading || !loader.data ? undefined : loader.data.url;

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const savingToast = toast.custom(<SavingToast />, {
        duration: 3000,
      });
      await saveImage();
      setSaving(false);
      toast.dismiss(savingToast);
      toast.custom(<SuccessToast />, { duration: 2000 });
    } catch (error: unknown) {
      setSaving(false);
      toast.custom(<ErrorToast />, { duration: 2000 });
      console.log(error);
    }
  }, [saveImage]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box
        data-testid="template-image"
        sx={[
          {
            position: "relative",
            borderRadius: "5px",
            border: "5px solid #eee",
            padding: "5px",
            overflow: "hidden",
            // add 20: 2 * (border + padding)
            width: dimensions.width + 20,
            height: dimensions.height + 20,
          },
        ]}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            boxSizing: "content-box",
            position: "relative",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="template-image-bg"
        >
          {!imageUrl && (
            <Box
              style={{
                display: "flex",
                marginTop: "8px",
                marginLeft: "8px",
              }}
            >
              <CircularProgress sx={{ width: "24px", height: "24px" }} />
            </Box>
          )}
        </Box>
      </Box>
      {format.toLowerCase() !== "communication" && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            data-analytics-id="comms-image-download"
            disabled={!imageUrl}
            onClick={() => void downloadFile(imageUrl ?? "", format, "png")}
          >
            Download Image
          </Button>
          <Button
            variant="outlined"
            startIcon={<BookmarkOutlinedIcon />}
            data-analytics-id="comms-image-save"
            disabled={!imageUrl}
            loading={saving}
            onClick={() => void handleSave()}
          >
            Save to Content Library
          </Button>
        </Stack>
      )}
    </Box>
  );
};

const SuccessToast: FC = () => (
  <Alert severity="success">
    Saved to your <Link href="/content-library">Content Library</Link>
  </Alert>
);

const SavingToast: FC = () => <Alert severity="info">Saving image...</Alert>;

const ErrorToast: FC = () => (
  <Alert severity="error" variant="outlined">
    There was an error saving your image. Please try again later.
  </Alert>
);
